import React from 'react';
import FollowupActions from './FollowupActions';

/**
 * Visas när byggkonsulten har skickat tidsförslag och väntar på svar från kund
 * Scenario A2: KONSULT_PROPOSAL_WAITING
 */
const KonsultProposalsWaiting = ({
  proposals,
  message,
  customerResponse,
  bookedFollowupSlot,
  estimatFollowup,
  onSuggestTime,
  onBookMeeting,
  onApproveForCustomer,
  onBookHomeVisit,
  approveLoading,
  isApproved = false,
  onCancelProposals,
  onCancelSingleProposal,
  isCancellingProposals = false,
}) => {
  const isAlreadyBooked = bookedFollowupSlot || estimatFollowup?.selectedSlot || estimatFollowup?.meetingAt;

  return (
    <div>
      <div className="text-sm text-gray-700 mb-4">
        <strong>Skickade förslag till kund</strong>
        {message && (
          <div className="mt-2 text-xs text-gray-600 p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
            <strong>Ditt meddelande:</strong> {message}
          </div>
        )}
      </div>

      <ul className="space-y-2 text-sm text-gray-700 mb-4">
        {proposals.map((proposal, index) => (
          /* w-fit: raden blir bara så bred som innehållet, så krysset hamnar
             intill tiden i stället för ute vid sektionens högerkant */
          <li
            key={index}
            className="flex w-fit max-w-full items-center gap-3 bg-yellow-50 border border-yellow-200 rounded px-3 py-2"
          >
            <span className="text-gray-900">{proposal.displayText}</span>
            <div className="flex items-center gap-2">
            {(() => {
              if (customerResponse?.status === 'replied') {
                return (
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                    Kund har svarat
                  </span>
                );
              } else if (customerResponse?.status === 'accepted') {
                return (
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                    Accepterat
                  </span>
                );
              } else {
                return (
                  <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                    Väntar på kundsvar
                  </span>
                );
              }
            })()}

            {/*
              Återkalla just den här tiden. Är det sista förslaget dras hela
              utskicket tillbaka — bekräftelsedialogen säger det.
            */}
            {!isAlreadyBooked && onCancelSingleProposal && proposal._id && (
              <button
                type="button"
                onClick={() => onCancelSingleProposal(proposal, proposals.length)}
                disabled={isCancellingProposals}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title={`Återkalla ${proposal.displayText}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            </div>
          </li>
        ))}
      </ul>

      {/* Visa kundens svar om de har svarat */}
      {customerResponse?.status === 'replied' && customerResponse?.message && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="text-sm font-medium text-blue-800 mb-2">💬 Kundens svar:</div>
          <div className="text-sm text-blue-700 italic">"{customerResponse.message}"</div>
          <div className="text-xs text-blue-600 mt-2">
            Mottaget {new Date(customerResponse.repliedAt).toLocaleString('sv-SE')}
          </div>
        </div>
      )}

      {/* Återkalla skickade tidsförslag (om inget bokats ännu) */}
      {!isAlreadyBooked && onCancelProposals && (
        <button
          type="button"
          onClick={onCancelProposals}
          disabled={isCancellingProposals}
          className="mb-3 px-2.5 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap inline-flex items-center gap-1 text-red-600 bg-white border border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={proposals.length > 1
            ? 'Återkalla samtliga skickade tidsförslag och avboka de preliminära tiderna. Enskilda förslag återkallas med krysset intill tiden.'
            : 'Återkalla tidsförslaget och avboka den preliminära tiden'}
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a4 4 0 010 8h-1m-9-8l4-4m-4 4l4 4" />
          </svg>
          {isCancellingProposals
            ? 'Återkallar...'
            : proposals.length > 1 ? 'Återkalla alla tidsförslag' : 'Återkalla tidsförslaget'}
        </button>
      )}

      {/* Dölj knappar om uppföljning redan är bokad */}
      {!isAlreadyBooked && (
        <FollowupActions
          onSuggestTime={onSuggestTime}
          onBookMeeting={onBookMeeting}
          onApproveForCustomer={onApproveForCustomer}
          onBookHomeVisit={onBookHomeVisit}
          approveLoading={approveLoading}
          isApproved={isApproved}
        />
      )}
    </div>
  );
};

export default KonsultProposalsWaiting;
